// Copy for /our-work/digital-cfo (src/pages/OurWorkDigitalCFO.tsx).

const en = {
  seo: {
    title: 'Digital CFO | Stratodata',
    description:
      'Management reporting & controlling for retail and e-commerce CFOs. From EBIT in bulk to a channel-level contribution walk — every channel and region read down to net contribution and EBIT, every month, with plan, forecast and AI.',
  },
  hero: {
    title: 'Digital CFO',
    lead: 'EBIT is the most important number. You need to see it accurately, per channel, every month. Do you see it that way today?',
    sub: 'Management reporting & controlling that takes you from EBIT in bulk to a channel-level contribution walk — with plan, forecast and AI on top.',
    ctaPrimary: 'Let\'s find your real EBIT',
    // Small line inside the primary button; empty = not shown
    ctaNote: '',
    ctaSecondary: 'How it works',
  },
  twoWorlds: {
    eyebrow: 'Two worlds of accounting',
    title: 'Every company keeps the books. Almost none can read profit.',
    text: 'Statutory accounting is a forced, recurring obligation. We don’t replace your accountant — we build the second world they never produce, from data that already exists.',
    statutoryTitle: 'Statutory accounting',
    statutorySub: 'Mandatory filing for the state. Every company has it.',
    statutory: [
      'All accounting in one place, not separated',
      'EBIT evaluated in bulk, monthly or quarterly',
      'No P&L streams per channel × region',
      'No planning tied to the P&L',
      'No automated forecasting',
      'Monthly reporting hand-built in Excel (~2 days/month)',
    ],
    managementTitle: 'Management accounting',
    managementTitleAccent: '(Digital CFO)',
    managementSub: 'Built to run the business correctly. Most companies don\'t have it, because it can\'t simply be pulled out of the accounting system.',
    management: [
      'Channels & regions in separate streams down to net contribution',
      'Accounts grouped to mirror each channel’s real cost structure',
      'Planning on every row — the full P&L, not just revenue',
      'Whole-P&L forecast, lightly reforecast on real drift',
      'Reported together, filterable by stream, fixed after monthly close',
      'Structured data connected to AI for advisory & anomaly detection',
    ],
  },
  model: {
    eyebrow: 'The model',
    title: 'A contribution walk for every channel',
    text: 'Each channel reads from gross revenue down to gross margin, then through variable cost-to-serve to net contribution — what it earns toward covering HQ. Central costs stay separate, on visible lines, down to EBIT.',
    steps: [
      { step: 'Gross revenue → Gross margin', detail: 'Revenue, returns, discounts and COGS, derived exactly from order, POS and stock data.' },
      { step: 'Gross margin → Net contribution', detail: 'Shipping, fulfilment, payment, performance marketing, commissions — the variable cost-to-serve per order.' },
      { step: 'Net contribution → EBIT', detail: 'Demand-gen, brand, and HQ overhead held on their own lines — long-term investments, never buried in a channel.' },
    ],
    note: 'E-commerce, brick-and-mortar, marketplace, B2B and direct sales each get a channel-specific template — combined to match exactly how you run, by channel and by country.',
    crossLink: 'See the walk in action — try the interactive Walk to Profit',
  },
  moat: {
    eyebrow: 'Why it’s a product, not advisory',
    title: 'We rebuild the split — in your data',
    p1Before: 'Splitting the general ledger by business structure is the hard part — but it’s mostly our data job, not your accounting task. Roughly ',
    p1Highlight: '70–80%',
    p1After: ' of the split is reconstructed by joining your GL to source systems you already have: orders, ad platforms, carriers, payment gateways and marketplace settlements.',
    p2: 'Only a small residual needs coding at source. Your accounting keeps working the way it does today, and we prepare the data for management decisions.',
    p3: 'And every allocated P&L reconciles back to your statutory totals. If the channels don’t tie to the books, the allocation is wrong — that control is our quality gate and your proof the numbers hold.',
    sourcesTitle: 'Joined from systems you already run',
  },
  activate: {
    eyebrow: 'On top of the data',
    title: 'Plan, forecast & AI',
    text: 'Once the structure holds, we activate it — one monthly source of truth, steered forward.',
    cards: [
      { title: 'Plan on every row', text: 'A budget on the full P&L by business structure — not just revenue. Built from history where it exists, from business logic where it doesn’t.' },
      { title: 'Continuous forecast', text: 'Whole-P&L forecasting via the Stratodata autoforecaster — light and continuous, not a quarterly mega-exercise.' },
      { title: 'One source of truth', text: 'A monthly report locked after close, plus a daily estimated view for leading signal between closes. Same structure, two cadences.' },
      { title: 'AI advisor', text: 'AI on the structured data for advisory insights, anomaly and audit checks, fee & FX analysis and what-if questions in plain language.' },
    ],
  },
  build: {
    eyebrow: 'How we build it',
    title: 'Core first, then layered',
    text: 'We ship the contribution walk with plan first, then layer forecasting, cash and the AI advisor onto the same structured data.',
    phases: [
      {
        title: 'Contribution Walk + Plan',
        description: 'Business structure, cost structure, account mapping, net contribution & EBIT per channel × country, plan on every row, and a monthly report fixed after close.',
      },
      {
        title: 'Forecast',
        description: 'Whole-P&L forecast via the Stratodata autoforecaster — light and continuous, reforecast only on real drift in margin or a key metric.',
      },
      {
        title: 'Cash & Receivables',
        description: 'Daily cash position across banks and countries from a simple bank statement feed, plus receivables by dimension to flag good-margin but bad-cash customers.',
      },
      {
        title: 'AI Layer',
        description: 'AI on the structured data and plan: advisory insights, anomaly and audit checks, fee & FX analysis, cash burn and runway — answered in plain language.',
      },
    ],
  },
  cta: {
    title: 'See EBIT Correctly, Every Month',
    text: 'Per channel, against plan and last year, with a forecast and an AI advisor on top. We get you there — on data you already have.',
    button: 'Build my contribution walk',
  },
};

const sk: typeof en = {
  seo: {
    title: 'Digital CFO | Stratodata',
    description:
      'Manažérsky reporting a controlling pre finančných riaditeľov v retaile a e-commerce. Namiesto súhrnného EBIT uvidíte Contribution Walk (kaskádu príspevku na úhradu) po kanáloch. Každý kanál a región až po čistý príspevok na úhradu a EBIT, každý mesiac, s plánom, prognózou a AI.',
  },
  hero: {
    title: 'Digital CFO',
    lead: 'EBIT je najdôležitejšie číslo. Potrebujete ho vidieť presne, za každý kanál a každý mesiac. Vidíte ho tak dnes?',
    sub: 'Manažérsky reporting a controlling, s ktorým sa od súhrnného EBIT dostanete ku Contribution Walk (kaskáde príspevku na úhradu) po kanáloch. Navrch pridáme plán, prognózu a AI.',
    ctaPrimary: 'Nájdime váš skutočný EBIT',
    ctaNote: '',
    ctaSecondary: 'Ako to funguje',
  },
  twoWorlds: {
    eyebrow: 'Dva svety účtovníctva',
    title: 'Účtovníctvo vedie každá firma. Zisk z neho nevyčíta takmer žiadna.',
    text: 'Finančné účtovníctvo je povinnosť, ktorú plníte znova a znova. Vášho účtovníka nenahrádzame. Z dát, ktoré už máte, vám postavíme druhý svet, ktorý od neho nikdy nedostanete.',
    statutoryTitle: 'Finančné účtovníctvo',
    statutorySub: 'Povinné výkazy pre štát. Robí ich každá firma.',
    statutory: [
      'Celé účtovníctvo na jednom mieste, bez rozdelenia',
      'EBIT hodnotený súhrnne, mesačne alebo štvrťročne',
      'Žiadna výsledovka po kanáloch a regiónoch',
      'Žiadne plánovanie naviazané na výsledovku',
      'Žiadna automatizovaná prognóza',
      'Mesačný reporting ručne skladaný v Exceli (~2 dni mesačne)',
    ],
    managementTitle: 'Manažérske účtovníctvo',
    managementTitleAccent: '(Digital CFO)',
    managementSub: 'Slúži na to, aby ste firmu riadili správne. Väčšina firiem ho nemá, lebo sa nedá jednoducho vytiahnuť z účtovníctva.',
    management: [
      'Kanály a regióny v samostatných líniách až po čistý príspevok na úhradu',
      'Účty zoskupené podľa skutočnej nákladovej štruktúry každého kanála',
      'Plán na každom riadku celej výsledovky, nielen na tržbách',
      'Prognóza celej výsledovky, ktorú upravíme len pri reálnej odchýlke',
      'Jeden report s filtrom podľa línie, uzamknutý po mesačnej uzávierke',
      'Štruktúrované dáta napojené na AI, ktorá radí a odhalí anomálie',
    ],
  },
  model: {
    eyebrow: 'Model',
    title: 'Contribution Walk pre každý kanál',
    text: 'Každý kanál čítame od hrubých tržieb po hrubú maržu a ďalej cez variabilné náklady na obsluhu až po čistý príspevok na úhradu. Ten ukazuje, koľko kanál zarobí na pokrytie centrály. Centrálne náklady ostávajú oddelene na viditeľných riadkoch až po EBIT.',
    steps: [
      { step: 'Hrubé tržby → Hrubá marža', detail: 'Tržby, vratky, zľavy a náklady na predaný tovar (COGS), odvodené presne z dát o objednávkach, pokladniciach a zásobách.' },
      { step: 'Hrubá marža → Čistý príspevok na úhradu', detail: 'Doprava, fulfilment, platby, výkonnostný marketing a provízie. Teda variabilné náklady na obsluhu jednej objednávky.' },
      { step: 'Čistý príspevok na úhradu → EBIT', detail: 'Budovanie dopytu, značka a réžia centrály majú vlastné riadky. Sú to dlhodobé investície, preto ich nikdy neschovávame v kanáli.' },
    ],
    note: 'E-shop, kamenné predajne, marketplace, B2B aj priamy predaj majú každý vlastnú šablónu. Skombinujeme ich presne podľa toho, ako fungujete, po kanáloch aj krajinách.',
    crossLink: 'Pozrite si Contribution Walk v praxi v interaktívnom Walk to Profit',
  },
  moat: {
    eyebrow: 'Prečo je to produkt, nie poradenstvo',
    title: 'Rozpočítanie postavíme nanovo – vo vašich dátach',
    p1Before: 'Rozdeliť hlavnú knihu podľa toho, ako firma reálne funguje, je najťažšia časť. Väčšinou je to však naša dátová práca, nie úloha vášho účtovníctva. Približne ',
    p1Highlight: '70–80 %',
    p1After: ' rozpočítania zrekonštruujeme prepojením hlavnej knihy so systémami, ktoré už máte: objednávky, reklamné platformy, dopravcovia, platobné brány a vyúčtovania z marketplaceov.',
    p2: 'Priamo pri účtovaní potom stačí označiť už len malý zvyšok. Vaše účtovníctvo tak môže fungovať ako doteraz a dáta pre manažérske rozhodnutia pripravíme my.',
    p3: 'Každá rozpočítaná výsledovka navyše zodpovedá súčtom vo vašom účtovníctve. Ak sa kanály nezhodujú s knihami, rozpočítanie je chybné. Touto kontrolou si strážime kvalitu a vy máte dôkaz, že čísla platia.',
    sourcesTitle: 'Prepojené zo systémov, ktoré už používate',
  },
  activate: {
    eyebrow: 'Nad dátami',
    title: 'Plán, prognóza a AI',
    text: 'Keď štruktúra sedí, začneme ju naplno používať. Máte jeden mesačný zdroj pravdy, podľa ktorého riadite ďalej.',
    cards: [
      { title: 'Plán na každom riadku', text: 'Rozpočet na celú výsledovku podľa štruktúry firmy, nielen na tržby. Kde máte historické dáta, staviame z nich. Kde chýbajú, vychádzame z vašich obchodných predpokladov.' },
      { title: 'Priebežná prognóza', text: 'Prognóza celej výsledovky cez Stratodata Autoforecaster. Ľahká a priebežná, bez náročného štvrťročného plánovania.' },
      { title: 'Jeden zdroj pravdy', text: 'Mesačný report uzamknutý po uzávierke a k nemu denný odhad, ktorý dáva včasný signál medzi uzávierkami. Rovnaká štruktúra, dva rytmy.' },
      { title: 'AI poradca', text: 'AI nad štruktúrovanými dátami radí, hľadá anomálie, robí auditné kontroly a analyzuje poplatky aj kurzové rozdiely. Na otázky typu „čo ak“ odpovedá v bežnej reči.' },
    ],
  },
  build: {
    eyebrow: 'Ako to staviame',
    title: 'Najprv jadro, potom ďalšie vrstvy',
    text: 'Najprv dodáme Contribution Walk s plánom. Potom na tie isté dáta pridáme prognózu, hotovosť a AI poradcu.',
    phases: [
      {
        title: 'Contribution Walk + plán',
        description: 'Štruktúra biznisu, nákladová štruktúra, mapovanie účtov, čistý príspevok na úhradu a EBIT za každý kanál a krajinu, plán na každom riadku a mesačný report uzamknutý po uzávierke.',
      },
      {
        title: 'Prognóza',
        description: 'Prognóza celej výsledovky cez Stratodata Autoforecaster. Ľahká a priebežná, upravíme ju len pri reálnej odchýlke v marži alebo dôležitej metrike.',
      },
      {
        title: 'Hotovosť a pohľadávky',
        description: 'Denný stav hotovosti naprieč bankami a krajinami z jednoduchého importu bankových výpisov. K tomu pohľadávky podľa dimenzií, aby ste odhalili zákazníkov s dobrou maržou, ale zlou platobnou morálkou.',
      },
      {
        title: 'AI vrstva',
        description: 'AI nad štruktúrovanými dátami a plánom radí, hľadá anomálie, robí auditné kontroly a analyzuje poplatky aj kurzové rozdiely. Ukáže, ako rýchlo míňate hotovosť a na ako dlho vám vystačí. Odpovedá v bežnej reči.',
      },
    ],
  },
  cta: {
    title: 'Správny EBIT. Každý mesiac.',
    text: 'Za každý kanál, oproti plánu aj minulému roku, s prognózou a AI poradcom navrch. Dostaneme vás tam s dátami, ktoré už máte.',
    button: 'Chcem vlastný Contribution Walk',
  },
};

export const digitalCfoCopy = { en, sk };
